import { z } from "zod";

import { looseArray } from "../looseParse";

const tapeSchema = z.union([z.enum(["A", "B", "C", "N", "O"]), z.string()]);

export const quoteSchema = z.object({
  symbol: z.string(),
  bid_timestamp: z.string(),
  bid: z.number(),
  bid_size: z.number(),
  bid_exchange: z.string(),
  ask_timestamp: z.string(),
  ask: z.number(),
  ask_size: z.number(),
  ask_exchange: z.string(),
  last_timestamp: z.string(),
  last: z.number(),
  last_size: z.number(),
  last_exchange: z.string(),
  last_day_closing: z.string(),
});

export const latestQuoteSchema = z.object({
  t: z.string(),
  bx: z.string(),
  bp: z.number(),
  bs: z.number(),
  ax: z.string(),
  ap: z.number(),
  as: z.number(),
  c: z.array(z.string()),
  z: tapeSchema,
});

const stockSnapshotTradeSchema = z.object({
  t: z.string(),
  x: z.string(),
  p: z.number(),
  s: z.number(),
  i: z.number(),
  c: z.array(z.string()),
  z: tapeSchema,
});

const snapshotBarSchema = z.object({
  t: z.string(),
  o: z.number(),
  h: z.number(),
  l: z.number(),
  c: z.number(),
  v: z.number(),
  n: z.number(),
  vw: z.number(),
});

export const stockSnapshotSchema = z.object({
  latestTrade: stockSnapshotTradeSchema.optional(),
  latestQuote: latestQuoteSchema.optional(),
  minuteBar: snapshotBarSchema.optional(),
  dailyBar: snapshotBarSchema.optional(),
  prevDailyBar: snapshotBarSchema.optional(),
});

export const stockSnapshotsResponseSchema = z.record(
  z.string(),
  stockSnapshotSchema,
);

export const clockSchema = z.object({
  timestamp: z.string(),
  is_open: z.boolean(),
  next_open: z.string(),
  next_close: z.string(),
});

export const marketPhaseSchema = z.enum([
  "closed",
  "pre",
  "core",
  "post",
  "overnight",
]);

export const marketStatusPeriodSchema = z.object({
  date: z.string(),
  from: z.string(),
  until: z.string(),
});

export const marketStatusSchema = z.object({
  active: z.boolean(),
  phase: marketPhaseSchema,
  current: marketStatusPeriodSchema,
  next: marketStatusPeriodSchema,
});

export const cryptoOrderbookEntrySchema = z.object({
  p: z.number(),
  s: z.number(),
});

export const cryptoOrderbookSchema = z.object({
  t: z.string(),
  b: z.array(cryptoOrderbookEntrySchema),
  a: z.array(cryptoOrderbookEntrySchema),
});

export const cryptoOrderbooksLatestSchema = z.object({
  orderbooks: z.record(z.string(), cryptoOrderbookSchema),
});

const cryptoSnapshotTradeSchema = z.object({
  i: z.number(),
  p: z.number(),
  s: z.number(),
  t: z.string(),
  tks: z.string(),
});

const cryptoSnapshotQuoteSchema = z.object({
  ap: z.number(),
  as: z.number(),
  bp: z.number(),
  bs: z.number(),
  t: z.string(),
});

export const cryptoSnapshotSchema = z.object({
  dailyBar: snapshotBarSchema.optional(),
  latestQuote: cryptoSnapshotQuoteSchema.optional(),
  latestTrade: cryptoSnapshotTradeSchema.optional(),
  minuteBar: snapshotBarSchema.optional(),
  prevDailyBar: snapshotBarSchema.optional(),
});

export const cryptoSnapshotsSchema = z.object({
  snapshots: z.record(z.string(), cryptoSnapshotSchema),
});

export const cryptoAssetSchema = z.object({
  id: z.string(),
  class: z.string(),
  exchange: z.string(),
  symbol: z.string(),
  name: z.string(),
  status: z.string(),
  tradable: z.boolean(),
  marginable: z.boolean(),
  maintenance_margin_requirement: z.number(),
  shortable: z.boolean(),
  easy_to_borrow: z.boolean(),
  fractionable: z.boolean(),
  min_order_size: z.string(),
  min_trade_increment: z.string(),
  price_increment: z.string(),
});

export const optionContractSchema = z.object({
  id: z.string(),
  symbol: z.string(),
  name: z.string(),
  status: z.string(),
  tradable: z.boolean(),
  expiration_date: z.string(),
  root_symbol: z.string(),
  underlying_symbol: z.string(),
  underlying_asset_id: z.string(),
  type: z.enum(["call", "put"]),
  style: z.enum(["american", "european"]),
  strike_price: z.string(),
  size: z.string(),
  // Pricing/interest metrics are nullable backend pointers: an unpriced or
  // never-traded contract sends them as `null`. Use `.nullish()` so such a
  // contract isn't dropped by the per-item `looseArray` below.
  open_interest: z.string().nullish(),
  open_interest_date: z.string().nullish(),
  close_price: z.string().nullish(),
  close_price_date: z.string().nullish(),
  multiplier: z.string(),
});

export const optionContractsResponseSchema = z.object({
  // Per-item: a single malformed contract shouldn't fail the whole chain page.
  option_contracts: looseArray(
    optionContractSchema,
    "optionContractsResponseSchema.option_contracts",
  ),
  // Gobroker's cursor response returns `next_page_token`. `page`/`limit` are
  // deprecated `omitempty` fields only populated when the request supplies
  // `page` (this client sends `limit` but not `page`), so a normal response
  // omits them. Requiring `page`/`limit` made every normal response fail the
  // envelope, fall back to the raw payload, and silently disable the per-item
  // parsing above.
  next_page_token: z.string().nullish(),
  page: z.number().optional(),
  limit: z.number().optional(),
});

const optionSnapshotBarSchema = z.object({
  t: z.string(),
  o: z.number(),
  h: z.number(),
  l: z.number(),
  c: z.number(),
  v: z.number(),
  n: z.number(),
  vw: z.number(),
});

const optionSnapshotSchema = z.object({
  latestQuote: z
    .object({
      ap: z.number(),
      as: z.number(),
      ax: z.string(),
      bp: z.number(),
      bs: z.number(),
      bx: z.string(),
      c: z.string(),
      t: z.string(),
    })
    .optional(),
  latestTrade: z
    .object({
      c: z.string(),
      p: z.number(),
      s: z.number(),
      t: z.string(),
      x: z.string(),
    })
    .optional(),
  dailyBar: optionSnapshotBarSchema.optional(),
  minuteBar: optionSnapshotBarSchema.optional(),
  prevDailyBar: optionSnapshotBarSchema.optional(),
  greeks: z
    .object({
      delta: z.number(),
      gamma: z.number(),
      rho: z.number(),
      theta: z.number(),
      vega: z.number(),
    })
    .optional(),
  impliedVolatility: z.number().optional(),
});

export const getOptionSnapshotsResponseSchema = z.object({
  next_page_token: z.string().nullable(),
  snapshots: z.record(z.string(), optionSnapshotSchema),
});
